'use client';

/**
 * Global Search Component
 * Command palette style search across entities, assessments, and more
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useCommandCenter } from '@/lib/command-center/CommandCenterContext';
import { ENTITY_TYPE_LABELS } from '@/lib/command-center/types';
import type { Entity, Assessment } from '@/lib/command-center/types';

interface SearchResult {
  id: string;
  type: 'entity' | 'assessment' | 'page';
  title: string;
  subtitle?: string;
  icon: string;
  href: string;
}

// Static pages for quick navigation
const STATIC_PAGES: SearchResult[] = [
  { id: 'dashboard', type: 'page', title: 'Dashboard', subtitle: 'Command Center home', icon: '📊', href: '/command-center' },
  { id: 'entities', type: 'page', title: 'Entities', subtitle: 'Browse all entities', icon: '📦', href: '/command-center/entities3' },
  { id: 'relationships', type: 'page', title: 'Relationships', subtitle: 'Entity relationships map', icon: '🕸️', href: '/command-center/relationships' },
  { id: 'assessments', type: 'page', title: 'Assessments', subtitle: 'View assessments', icon: '📋', href: '/command-center/assessments' },
  { id: 'compliance', type: 'page', title: 'Compliance Dashboard', subtitle: 'Compliance status overview', icon: '🛡️', href: '/command-center/compliance' },
  { id: 'sync', type: 'page', title: 'ArchiMate Sync', subtitle: 'Sync with ArchiMate', icon: '🔄', href: '/command-center/sync' },
  { id: 'activity', type: 'page', title: 'Activity Log', subtitle: 'Recent changes', icon: '📜', href: '/command-center/activity' },
  { id: 'matches', type: 'page', title: 'Match Suggestions', subtitle: 'Review entity matches', icon: '🔗', href: '/command-center/matches' },
  { id: 'import', type: 'page', title: 'Import', subtitle: 'Import entities', icon: '📥', href: '/command-center/import' },
];

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const router = useRouter();
  const { workspaceId } = useCommandCenter();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(i => Math.min(i + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(i => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            handleSelect(results[selectedIndex]);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, results, selectedIndex, onClose]);

  // Search function
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      // Show static pages when no query
      setResults(STATIC_PAGES);
      return;
    }

    if (!workspaceId) {
      setResults(STATIC_PAGES.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
      ));
      return;
    }

    setLoading(true);

    try {
      // Search entities
      const entitiesRes = await fetch(
        `/api/secure/command-center/entities?workspace_id=${workspaceId}&search=${encodeURIComponent(searchQuery)}&pageSize=10`
      );
      const entitiesData = entitiesRes.ok ? await entitiesRes.json() : { data: [] };
      const entities: Entity[] = entitiesData.data || [];

      // Search assessments
      const assessmentsRes = await fetch(
        `/api/secure/command-center/assessments?workspace_id=${workspaceId}&search=${encodeURIComponent(searchQuery)}&pageSize=5`
      );
      const assessmentsData = assessmentsRes.ok ? await assessmentsRes.json() : { data: [] };
      const assessments: Assessment[] = assessmentsData.data || [];

      // Combine results
      const searchResults: SearchResult[] = [];

      // Add matching pages first
      const matchingPages = STATIC_PAGES.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      searchResults.push(...matchingPages);

      // Add entities
      for (const entity of entities) {
        searchResults.push({
          id: entity.id,
          type: 'entity',
          title: entity.name,
          subtitle: ENTITY_TYPE_LABELS[entity.entity_type],
          icon: getEntityIcon(entity.entity_type),
          href: `/command-center/entities/${entity.id}`,
        });
      }

      // Add assessments
      for (const assessment of assessments) {
        searchResults.push({
          id: assessment.id,
          type: 'assessment',
          title: assessment.name,
          subtitle: `${assessment.tool_id} - ${assessment.score_overall || 0}%`,
          icon: '📋',
          href: `/command-center/assessments?selected=${assessment.id}`,
        });
      }

      setResults(searchResults);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Search error:', error);
      setResults(STATIC_PAGES);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 200);

    return () => clearTimeout(timer);
  }, [query, performSearch]);

  const handleSelect = (result: SearchResult) => {
    router.push(result.href);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      {/* Search Modal */}
      <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-full max-w-xl">
        <div className="bg-surface border border-border rounded-xl shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b border-border-muted">
            <svg className="w-5 h-5 text-foreground-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search entities, assessments, pages..."
              className="flex-1 bg-transparent text-foreground placeholder-foreground-muted outline-none text-lg"
            />
            {loading && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent"></div>
            )}
            <kbd className="hidden sm:inline-block px-2 py-0.5 bg-background text-foreground-tertiary text-xs rounded">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {results.length === 0 && query && !loading ? (
              <div className="p-8 text-center text-foreground-tertiary">
                No results found for &ldquo;{query}&rdquo;
              </div>
            ) : (
              <div className="py-2">
                {results.map((result, index) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      index === selectedIndex ? 'bg-accent-muted' : 'hover:bg-cream/5'
                    }`}
                  >
                    <span className="text-xl">{result.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${index === selectedIndex ? 'text-accent' : 'text-foreground'}`}>
                        {result.title}
                      </p>
                      {result.subtitle && (
                        <p className="text-foreground-tertiary text-sm truncate">{result.subtitle}</p>
                      )}
                    </div>
                    <span className="text-foreground-muted text-xs uppercase">{result.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-border-muted text-foreground-muted text-xs">
            <div className="flex items-center gap-4">
              <span><kbd className="px-1.5 py-0.5 bg-background rounded">↑</kbd> <kbd className="px-1.5 py-0.5 bg-background rounded">↓</kbd> Navigate</span>
              <span><kbd className="px-1.5 py-0.5 bg-background rounded">Enter</kbd> Select</span>
            </div>
            <span><kbd className="px-1.5 py-0.5 bg-background rounded">Esc</kbd> Close</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function getEntityIcon(type: string): string {
  const icons: Record<string, string> = {
    technology: '💻',
    infrastructure: '🖥️',
    application: '📱',
    data_store: '🗄️',
    api: '🔌',
    team: '👥',
    vendor: '🏢',
    process: '⚙️',
    capability: '🎯',
    product: '📦',
    stakeholder: '👤',
    compliance_framework: '🛡️',
    control: '🔒',
    risk: '⚠️',
    policy: '📜',
    architecture_decision: '📐',
    tech_debt: '🔧',
    migration_plan: '🚀',
    incident: '🚨',
    sla: '📝',
    metric: '📈',
  };
  return icons[type] || '📦';
}
